import cn from 'classnames';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, useToast } from '@economic/gaudi';
import ExternalLink from '../../../shared/components/ExternalLink';
import { t, tBB } from '../../../shared/components/l10n/l10n';
import ConfirmationDialog from '../../../shared/components/modals/ConfirmationDialog';
import DialogTrigger from '../../../shared/components/ui/primitives/DialogTrigger';
import internalApiClient from '../../../shared/util/api/internalApiClient';
import date from '../../../shared/util/utilities/date';
import DpaEditDialog from '../DpaEditDialog';
import IconCheck from './icon-check';
import { PlanInfo, UpgradePlanInfo } from './shared';
import { useCurrentAgreement } from '@economic/react-app';

interface Props {
    data: UpgradePlanInfo;
    selectPlan: (plan: PlanInfo) => void;
    downloadDpaAgreement: () => void;
    hide: () => void;
}

export default function ChoosePlanPage({ data, selectPlan, downloadDpaAgreement, hide }: Props) {
    const toast = useToast();
    const agreement = useCurrentAgreement();
    const location = useLocation();
    const navigate = useNavigate();

    const handleTrialExtension = async () => {
        try {
            const response = await internalApiClient.post(`/company/extendTrial`);
            hide();
            window.top?.refreshRouting();
            toast.success(
                tBB('TOAST_SUBSCRIPTION_TRIAL_EXTEND_SUCCESS', {
                    EXPIRYDATE: date.format(date.parseFromISOString(response.data?.expiryDate)),
                })
            );

            if (location.pathname === '/home') {
                // When on /home we need to reload in order for the UpgradeWidget to rerender
                navigate('/', { replace: true });
            }
        } catch (error) {
            toast.error(t`TOAST_SUBSCRIPTION_TRIAL_EXTEND_ERROR`);
        }
    };

    const {
        generalFeatures,
        plans,
        shouldSeeDpaTermsSection,
        isEconInvoicePaidViaLevService,
        subscriptionTermsOnclick,
        isPlanChangeNotificationVisible,
        planChangeNotificationHtml,
    } = data;

    const FeatureStyle = 'flex items-center text-grey-darkest text-base';

    const isTrialExpired = agreement.subscription.trial?.expiryDate
        ? new Date(agreement.subscription.trial.expiryDate).getTime() < Date.now()
        : false;

    return (
        <div className={cn('plan-upgrade-dialog', { 'relative bg-white': isTrialExpired })}>
            <h1>
                {t(
                    isTrialExpired
                        ? 'SUBSCRIPTIONSTATUS_UPGRADEEXPIREDTRIAL_HEADLINE'
                        : 'SUBSCRIPTIONSTATUS_UPGRADETRIAL_HEADLINE'
                )}
            </h1>
            <div className="flex flex-row justify-between items-center">
                <p className="w-128">
                    {t(
                        isTrialExpired
                            ? 'SUBSCRIPTIONSTATUS_UPGRADEEXPIREDTRIAL_SUBHEADLINE1'
                            : 'SUBSCRIPTIONSTATUS_UPGRADETRIAL_SUBHEADLINE1'
                    )}
                    {t(
                        isTrialExpired
                            ? 'SUBSCRIPTIONSTATUS_UPGRADEEXPIREDTRIAL_SUBHEADLINE2'
                            : 'SUBSCRIPTIONSTATUS_UPGRADETRIAL_SUBHEADLINE2'
                    )}
                </p>

                {agreement.subscription.trial?.canExtendTrial && (
                    <p>
                        <span className="text-base mr-2">{t`SUBSCRIPTIONSTATUS_TRIALEXPIRED_EXTEND_TEXT`}</span>
                        <Button
                            modal={modalProps => (
                                <Modal {...modalProps}>
                                    {({ hide }) => (
                                        <ConfirmationDialog
                                            label={t`SUBSCRIPTIONSTATUS_TRIALEXPIRED_EXTEND_CONFIRMATION_TITLE`}
                                            message={t`SUBSCRIPTIONSTATUS_TRIALEXPIRED_EXTEND_CONFIRMATION_TEXT`}
                                            onCancel={hide}
                                            onConfirm={handleTrialExtension}
                                        />
                                    )}
                                </Modal>
                            )}
                            appearance="primary">
                            <span className="text-xs w-32 max-w-48">{t`SUBSCRIPTIONSTATUS_TRIALEXPIRED_EXTEND_BUTTON`}</span>
                        </Button>
                    </p>
                )}
            </div>

            <div className="flex plans">
                {plans.map(plan => (
                    <div
                        key={plan.id}
                        className={cn('plan bg-white rounded-sm flex-1 flex flex-col mt-16 shadow-lg', {
                            'mt-12 z-0 relative shadow-lg': plan.isRecommended,
                        })}>
                        {plan.isRecommended && (
                            <section className="absolute w-full bg-blue text-white -mt-8 h-8 text-center leading-8 p-0 font-bold rounded-tl rounded-tr">
                                {plan.recommendedText}
                            </section>
                        )}
                        <section className="bg-white flex flex-col items-center pb-10 pt-6">
                            <span className="text-grey-darkest text-base font-bold text-center">{plan.name}</span>
                            <span className="text-grey-darkest text-3xl font-bold h-8">{plan.price}</span>
                            <span className="text-grey-darkest text-xs mt-1">{plan.price2}</span>
                        </section>
                        <Button
                            appearance={plan.isRecommended ? 'primary' : 'default'}
                            className="-mt-6 mb-6 self-center min-w-0 w-32"
                            onClick={() => selectPlan(plan)}>{t`SUBSCRIPTIONSTATUS_PLANSTATUS_SELECT`}</Button>

                        <ul className="features pl-0">
                            {generalFeatures.map((f, index) => (
                                <li className={FeatureStyle} key={index}>
                                    <IconCheck />
                                    {f}
                                </li>
                            ))}
                            <li className={FeatureStyle}>
                                <IconCheck />
                                {plan.transactions} {t`SUBSCRIPTIONSTATUS_SIDEBAR_MAXTRANSACTIONSPERYEAR2`}
                            </li>
                            {plan.optionalFeatures.map(feature => (
                                <li key={feature.id} className={FeatureStyle}>
                                    {feature.accessInPlan ? (
                                        <>
                                            <IconCheck />
                                            {feature.name}
                                        </>
                                    ) : (
                                        <span className="inline-block text-center w-8 leading-8">-</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            <p className="mt-3 ml-3">
                {tBB(`SUBSCRIPTIONSTATUS_SEEPRICING`, {
                    __LINK: content => <ExternalLink href="https://www.e-conomic.dk/priser">{content}</ExternalLink>,
                })}
            </p>

            {/* <ul className="list-none pl-3 mt-3 text-sm">
                {footerTerms
                    .split('<br>')
                    .filter(r => !!r)
                    .map((footerTerm, index) => (
                        <li key={index}>{footerTerm}</li>
                    ))}
            </ul> */}
            <div className="linksAndPlanChangeContainer flex">
                <ul className="list-none pl-0 px-3 leading-8">
                    <li className="px-3">
                        <ExternalLink
                            href={subscriptionTermsOnclick}>{t`SUBSCRIPTIONSTATUS_READSUBSCRIPTIONTERMSHERE`}</ExternalLink>
                    </li>
                    {shouldSeeDpaTermsSection && (
                        <>
                            <li>
                                <DialogTrigger dialog={({ hide }) => <DpaEditDialog hide={hide} />} width={600}>
                                    {({ show }) => (
                                        <Button
                                            appearance="discrete"
                                            onClick={show}>{t`TERMSCONDITIONS_SUBSCRIPTION_PAGE_EDIT_DPA`}</Button>
                                    )}
                                </DialogTrigger>
                            </li>
                            <li>
                                <Button
                                    appearance="discrete"
                                    onClick={downloadDpaAgreement}>{t`TERMSCONDITIONS_SUBSCRIPTION_PAGE_DOWNLOAD_DPA`}</Button>
                            </li>
                            {isEconInvoicePaidViaLevService && <li>{t`FORM_LICENSESTATUS_INVOICEPAIDVIALS`}</li>}
                        </>
                    )}
                </ul>
                {/* This code is going away in end of Dec (2019) ref ticket EC-24592 */}
                {isPlanChangeNotificationVisible && (
                    <>
                        <div className="flex bg-grey p-1">
                            <div>
                                <span className="p-px bg-blue text-white mx-1 my-0 font-bold rounded text-center inline-block w-8">
                                    !
                                </span>
                            </div>
                            <div
                                className="plans__price_change_notification_snackbar__text"
                                dangerouslySetInnerHTML={{ __html: planChangeNotificationHtml }}></div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
